"use client";

import React from "react";
import { MessageCircle, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function getSafeHref(href?: string | null) {
  if (!href) return null;
  const cleanHref = href.trim();
  if (!cleanHref || cleanHref === "#") return null;
  return cleanHref;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="27246" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 animate-pulse rounded-sm bg-[hsl(220_15%_20%)]" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => {
      const safeHref = getSafeHref(item.href);
      return <li key={item.id} data-index={i}>
            {safeHref ? <Link to={safeHref} className="font-sans text-sm font-medium tracking-wide text-[hsl(220_10%_85%)] transition-colors hover:text-[hsl(45_40%_50%)]">
                {item.label}
              </Link> : <span className="cursor-default font-sans text-sm font-medium tracking-wide text-[hsl(220_10%_60%)]">
                {item.label}
              </span>}
          </li>;
    })}
    </ul>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const quickLinks = [{
    icon: MessageCircle,
    href: "https://wa.me/527713940102",
    label: "WhatsApp",
    newTab: true
  }, {
    icon: Mail,
    href: "mailto:nunayarte@gmail.com",
    label: "Correo",
    newTab: false
  }, {
    icon: Phone,
    href: "tel:+527713940102",
    label: "Llamar",
    newTab: false
  }];
  return <section id="pie-de-pagina-sagrado" className="border-t border-[hsl(220_15%_15%)] bg-[hsl(220_15%_10%)] py-16 text-[hsl(45_20%_95%)]">
      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col items-start gap-6">
            <Link to="/" aria-label="Ir al inicio">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580544/img/el_atelier_de_nuna_logo.png" alt="El Atelier de NuNa" className="h-7 w-auto object-contain opacity-90 transition-opacity hover:opacity-100" />
            </Link>

            <p className="max-w-xs font-sans text-sm font-light leading-relaxed tracking-normal text-[hsl(220_10%_80%)]">
              Curaduría de cuarzos, velas y objetos con intención seleccionados
              para acompañar tu espacio, tu energía y tus rituales cotidianos.
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-medium tracking-wider text-[hsl(45_40%_50%)]">
              Colecciones
            </h3>

            <MenuProvider menu_id="51" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-medium tracking-wider text-[hsl(45_40%_50%)]">
              Contacto
            </h3>

            <ul className="flex flex-col gap-4 font-sans text-sm font-light text-[hsl(220_10%_80%)]">
              <li className="flex items-start gap-3" data-index="0">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(45_40%_50%)]" />
                <span>Pachuca, Hidalgo, México</span>
              </li>

              <li className="flex items-center gap-3" data-index="1">
                <Phone className="h-4 w-4 shrink-0 text-[hsl(45_40%_50%)]" />
                <Link to="tel:+527713940102" className="transition-colors hover:text-[hsl(45_40%_50%)]">
                  +52 771 394 0102
                </Link>
              </li>

              <li className="flex items-center gap-3" data-index="2">
                <Mail className="h-4 w-4 shrink-0 text-[hsl(45_40%_50%)]" />
                <Link to="mailto:nunayarte@gmail.com" className="transition-colors hover:text-[hsl(45_40%_50%)]">
                  nunayarte@gmail.com
                </Link>
              </li>

              <li className="flex items-start gap-3" data-index="3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(45_40%_50%)]" />
                <div className="flex flex-col gap-1">
                  <span>Atención personalizada por correo y WhatsApp</span>
                  <span>Respuesta según disponibilidad</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-medium tracking-wider text-[hsl(45_40%_50%)]">
              Conexión directa
            </h3>

            <div className="flex gap-4">
              {quickLinks.map((item, i) => {
              const Icon = item.icon;
              return <Link key={i} to={item.href} newTab={item.newTab} className="group flex h-10 w-10 items-center justify-center rounded-full border border-[hsl(220_15%_25%)] bg-[hsl(220_15%_15%)] transition-all duration-300 hover:border-[hsl(45_40%_50%)] hover:bg-[hsl(45_40%_50%)]" aria-label={item.label} data-index={i}>
                    <Icon className="h-4 w-4 text-[hsl(220_10%_90%)] transition-colors group-hover:text-[hsl(220_15%_10%)]" />
                  </Link>;
            })}
            </div>

            <p className="mt-2 font-sans text-xs font-light leading-relaxed text-[hsl(220_10%_60%)]">
              Pagos seguros en línea y envíos dentro de México.
            </p>
          </div>
        </div>

        <Separator className="mb-8 bg-[hsl(220_15%_20%)]" />

        <div className="flex flex-col items-center justify-between gap-4 font-sans text-xs font-light text-[hsl(220_10%_60%)] md:flex-row">
          <p>&copy; {currentYear} El Atelier de NuNa. Todos los derechos reservados.</p>

          <p className="font-medium uppercase tracking-widest text-[hsl(45_40%_50%)]">
            Energía con intención
          </p>
        </div>
      </div>
    </section>;
}